import React, { useState } from 'react';
import { ArrowLeft, Plus, Calendar, Edit2, Trash2, Power, AlertTriangle, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useMasterData } from '../../../context/MasterDataContext';
import { TahunAjaranRecord, NewTahunAjaranData } from '../../../types';
import { PortalGuruConfirmationModal } from '../PortalGuruConfirmationModal';

export const PortalGuruKelolaTahunAjaran: React.FC = () => {
    const navigate = useNavigate();
    const { tahunAjaranRecords, addTahunAjaran, updateTahunAjaran, deleteTahunAjaran } = useMasterData();

    const [isFormModalOpen, setIsFormModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [editingRecord, setEditingRecord] = useState<TahunAjaranRecord | null>(null);
    const [deletingRecord, setDeletingRecord] = useState<TahunAjaranRecord | null>(null);

    // Form state
    const [nama, setNama] = useState('');
    const [tanggalMulai, setTanggalMulai] = useState('');
    const [tanggalAkhir, setTanggalAkhir] = useState('');
    const [formError, setFormError] = useState('');

    const activeCount = tahunAjaranRecords.filter(r => r.status === 'Aktif').length;

    const resetForm = () => {
        setNama('');
        setTanggalMulai('');
        setTanggalAkhir('');
        setEditingRecord(null);
        setFormError('');
    };

    const handleOpenAdd = () => {
        resetForm();
        setIsFormModalOpen(true);
    };

    const handleOpenEdit = (record: TahunAjaranRecord) => {
        setEditingRecord(record);
        setNama(record.nama);
        // Format dates for input type="date"
        try {
            const startStr = typeof record.tanggalMulai === 'string' ? new Date(record.tanggalMulai).toISOString().split('T')[0] : record.tanggalMulai instanceof Date ? record.tanggalMulai.toISOString().split('T')[0] : '';
            const endStr = typeof record.tanggalAkhir === 'string' ? new Date(record.tanggalAkhir).toISOString().split('T')[0] : record.tanggalAkhir instanceof Date ? record.tanggalAkhir.toISOString().split('T')[0] : '';
            setTanggalMulai(startStr || '');
            setTanggalAkhir(endStr || '');
        } catch(e) {
            console.error("Date parsing error", e);
        }
        setIsFormModalOpen(true);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setFormError('');

        if (!nama || !tanggalMulai || !tanggalAkhir) {
            setFormError("Semua kolom wajib diisi.");
            return;
        }

        const start = new Date(tanggalMulai);
        const end = new Date(tanggalAkhir);

        if (end <= start) {
            setFormError("Tanggal akhir harus setelah tanggal mulai.");
            return;
        }

        try {
            if (editingRecord) {
                await updateTahunAjaran(editingRecord.id, {
                    nama,
                    tanggalMulai: start,
                    tanggalAkhir: end
                });
            } else {
                // Determine initial status based on rules
                const initialStatus = tahunAjaranRecords.length === 0 ? 'Aktif' : 'Non Aktif';

                await addTahunAjaran({
                    nama,
                    tanggalMulai: start,
                    tanggalAkhir: end,
                    status: initialStatus
                });
            }
            setIsFormModalOpen(false);
            resetForm();
        } catch (error: any) {
            setFormError("Terjadi kesalahan: " + error.message);
        }
    };

    const handleToggleStatus = async (record: TahunAjaranRecord) => {
        try {
            if (record.status === 'Aktif') {
                // Rule: Cannot turn off the only active record
                if (activeCount <= 1) {
                    alert("Tidak dapat menonaktifkan tahun ajaran ini karena harus ada minimal 1 tahun ajaran yang aktif.");
                    return;
                }
                await updateTahunAjaran(record.id, { status: 'Non Aktif' });
            } else {
                // Rule: When turning ON, we must turn OFF the currently active ones
                // to maintain "only one active" rule
                const currentlyActive = tahunAjaranRecords.filter(r => r.status === 'Aktif');

                // First turn off existing
                for (const activeRec of currentlyActive) {
                     await updateTahunAjaran(activeRec.id, { status: 'Non Aktif' });
                }

                // Then turn on the selected one
                await updateTahunAjaran(record.id, { status: 'Aktif' });
            }
        } catch (error) {
            console.error("Error toggling status:", error);
            alert("Gagal mengubah status.");
        }
    };

    const confirmDelete = (record: TahunAjaranRecord) => {
        if (record.status === 'Aktif' && activeCount <= 1) {
            alert("Tidak dapat menghapus tahun ajaran yang sedang aktif.");
            return;
        }
        setDeletingRecord(record);
        setIsDeleteModalOpen(true);
    };

    const executeDelete = async () => {
        if (deletingRecord) {
            try {
                await deleteTahunAjaran(deletingRecord.id);
                setIsDeleteModalOpen(false);
                setDeletingRecord(null);
            } catch(e) {
                 alert("Gagal menghapus.");
            }
        }
    };

    const formatDateStr = (dateVal: any) => {
        try {
            const d = new Date(dateVal);
            return d.toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' });
        } catch {
            return '-';
        }
    }

    return (
        <div className="min-h-screen bg-gray-50 pb-20 font-sans">
            {/* Header */}
            <div className="bg-white border-b sticky top-0 z-30 shadow-sm">
                <div className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => navigate(-1)}
                            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                        >
                            <ArrowLeft className="w-5 h-5 text-gray-600" />
                        </button>
                        <div>
                            <h1 className="text-lg font-bold text-gray-900 leading-tight">Tahun Ajaran</h1>
                            <p className="text-xs text-gray-500 font-medium">Pengaturan Sistem</p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="p-4 max-w-3xl mx-auto space-y-4">
                <div className="flex justify-between items-center bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                     <div className="flex items-center gap-3">
                         <div className="bg-brand-primary/10 p-2 rounded-lg">
                             <Calendar className="w-6 h-6 text-brand-primary" />
                         </div>
                         <div>
                             <h2 className="text-md font-bold text-gray-800">Daftar Tahun Ajaran</h2>
                             <p className="text-xs text-gray-500">{tahunAjaranRecords.length} Data tersimpan</p>
                         </div>
                     </div>
                     <button
                        onClick={handleOpenAdd}
                        className="flex items-center gap-1.5 bg-brand-primary text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-brand-primary/90 transition-colors shadow-sm"
                     >
                         <Plus className="w-4 h-4" />
                         <span className="hidden sm:inline">Tambah Data</span>
                         <span className="sm:hidden">Tambah</span>
                     </button>
                </div>

                <div className="space-y-3">
                    {tahunAjaranRecords.length === 0 ? (
                        <div className="text-center py-10 bg-white rounded-xl shadow-sm border border-gray-100">
                            <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                            <p className="text-gray-500 font-medium">Belum ada data tahun ajaran.</p>
                        </div>
                    ) : (
                        tahunAjaranRecords.sort((a,b) => b.nama.localeCompare(a.nama)).map(record => (
                            <div key={record.id} className={`bg-white rounded-xl shadow-sm border p-4 transition-all ${record.status === 'Aktif' ? 'border-brand-primary ring-1 ring-brand-primary/20' : 'border-gray-200 opacity-80'}`}>
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                            <h3 className="text-lg font-bold text-gray-900">{record.nama}</h3>
                                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${record.status === 'Aktif' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                                                {record.status}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-4 text-xs text-gray-500 mt-2">
                                             <div className="flex items-center gap-1.5">
                                                 <span className="font-medium text-gray-700">Mulai:</span> {formatDateStr(record.tanggalMulai)}
                                             </div>
                                             <div className="flex items-center gap-1.5">
                                                 <span className="font-medium text-gray-700">Akhir:</span> {formatDateStr(record.tanggalAkhir)}
                                             </div>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2 sm:justify-end pt-3 sm:pt-0 border-t sm:border-t-0 border-gray-100">
                                         <button
                                             onClick={() => handleToggleStatus(record)}
                                             className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                                                 record.status === 'Aktif'
                                                 ? 'bg-amber-50 text-amber-700 hover:bg-amber-100'
                                                 : 'bg-green-50 text-green-700 hover:bg-green-100'
                                             }`}
                                         >
                                             <Power className="w-3.5 h-3.5" />
                                             {record.status === 'Aktif' ? 'Nonaktifkan' : 'Aktifkan'}
                                         </button>
                                         <button
                                             onClick={() => handleOpenEdit(record)}
                                             className="p-1.5 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                                             title="Edit"
                                         >
                                             <Edit2 className="w-4 h-4" />
                                         </button>
                                         <button
                                             onClick={() => confirmDelete(record)}
                                             className={`p-1.5 rounded-lg transition-colors ${record.status === 'Aktif' && activeCount <= 1 ? 'text-gray-400 bg-gray-50 cursor-not-allowed' : 'text-red-600 bg-red-50 hover:bg-red-100'}`}
                                             disabled={record.status === 'Aktif' && activeCount <= 1}
                                             title="Hapus"
                                         >
                                             <Trash2 className="w-4 h-4" />
                                         </button>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Form Modal */}
            {isFormModalOpen && (
                <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex justify-center items-end sm:items-center">
                    <div className="bg-white w-full sm:w-[400px] sm:rounded-2xl rounded-t-2xl shadow-xl overflow-hidden flex flex-col max-h-[90vh]">
                        <div className="flex justify-between items-center px-4 py-3 border-b bg-gray-50">
                            <h2 className="text-base font-bold text-gray-800">{editingRecord ? 'Edit Tahun Ajaran' : 'Tambah Tahun Ajaran'}</h2>
                            <button onClick={() => setIsFormModalOpen(false)} className="p-1.5 bg-gray-200 text-gray-600 rounded-full hover:bg-gray-300">
                                <X size={16} />
                            </button>
                        </div>

                        <div className="p-4 overflow-y-auto">
                            {formError && (
                                <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg flex items-start gap-2">
                                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                                    <span>{formError}</span>
                                </div>
                            )}

                            <form id="ta-form" onSubmit={handleSubmit} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">Nama Tahun Ajaran</label>
                                    <input
                                        type="text"
                                        value={nama}
                                        onChange={(e) => setNama(e.target.value)}
                                        placeholder="Contoh: 2024/2025"
                                        className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none text-sm"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">Tanggal Mulai</label>
                                    <input
                                        type="date"
                                        value={tanggalMulai}
                                        onChange={(e) => setTanggalMulai(e.target.value)}
                                        className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none text-sm"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">Tanggal Akhir</label>
                                    <input
                                        type="date"
                                        value={tanggalAkhir}
                                        onChange={(e) => setTanggalAkhir(e.target.value)}
                                        className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none text-sm"
                                        required
                                    />
                                </div>
                            </form>
                        </div>

                        <div className="p-4 bg-gray-50 border-t flex justify-end gap-3">
                            <button
                                onClick={() => setIsFormModalOpen(false)}
                                className="px-4 py-2 text-sm font-semibold text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                                Batal
                            </button>
                            <button
                                type="submit"
                                form="ta-form"
                                className="px-4 py-2 text-sm font-semibold text-white bg-brand-primary rounded-lg hover:bg-brand-secondary shadow-sm"
                            >
                                Simpan
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Custom Delete Confirmation Modal */}
            <PortalGuruConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={() => {
                    setIsDeleteModalOpen(false);
                    setDeletingRecord(null);
                }}
                onConfirm={executeDelete}
                title="Hapus Tahun Ajaran"
                message={`Apakah Anda yakin ingin menghapus tahun ajaran "${deletingRecord?.nama}"? Data yang sudah dihapus mungkin berdampak pada rekam jejak sistem.`}
                confirmText="Ya, Hapus"
                cancelText="Batal"
                type="danger"
            />
        </div>
    );
};
